<script setup lang="ts">
import { Loader2, TriangleAlert } from 'lucide-vue-next'
import type { Audience } from '@/types/types'
import type { TriggerSettings } from '../types/marketingFlow'

const { t } = useI18n()

const props = defineProps<{
  isOpen: boolean
  handleClose: (isOpen: boolean) => void
  triggerSettings?: TriggerSettings
  audiences?: Audience[]
  audienceCount?: number | null
  showSaveAndQuitButton?: boolean
  isProcessing?: boolean
}>()

const emit = defineEmits<{
  (e: 'on-activate', executeOnExistingMembers?: boolean | null): void
  (e: 'on-save-and-quit'): void
}>()

const executeOnExistingMembers = ref<{
  value: 'all' | 'new'
} | null>(null)

const showAudienceOptions = computed(
  () => props.triggerSettings?.triggerType === 'SEGMENT_ENTRY' && props.triggerSettings?.selectedAudience
)

const selectedAudience = computed(() => {
  if (!showAudienceOptions.value) return null

  return (
    props.audiences?.find((audience) => Number(audience.id) === Number(props.triggerSettings?.selectedAudience)) ?? null
  )
})

const handleConfirm = () => {
  if (props.isProcessing) return

  if (showAudienceOptions.value) {
    emit('on-activate', executeOnExistingMembers.value?.value === 'all')
  } else {
    emit('on-activate')
  }
}

const handleClose = (isOpen: boolean) => {
  if (props.isProcessing) return
  props.handleClose(isOpen)
}

const choices = computed(() => [
  {
    id: 1,
    title: t('pages.marketingFlow.marketingFlowEditPage.activationModal.segmentEntry.option.new.title'),
    description: t('pages.marketingFlow.marketingFlowEditPage.activationModal.segmentEntry.option.new.description'),
    value: 'new',
    disabled: props.isProcessing
  },
  {
    id: 2,
    title: t('pages.marketingFlow.marketingFlowEditPage.activationModal.segmentEntry.option.all.title', {
      count: props.audienceCount ?? 0
    }),
    description: t('pages.marketingFlow.marketingFlowEditPage.activationModal.segmentEntry.option.all.description'),
    value: 'all',
    disabled: props.isProcessing
  }
])

onUnmounted(() => {
  executeOnExistingMembers.value = null
})
</script>

<template>
  <BaseModal
    v-if="!showAudienceOptions"
    :is-open="isOpen"
    size="lg"
    :show-footer="false"
    :is-closable="!isProcessing"
    @close="handleClose(false)"
  >
    <div class="flex w-full flex-row justify-center">
      <div class="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
        <TriangleAlert class="h-6 w-6 text-yellow-500" />
      </div>
    </div>

    <p class="mt-5 text-center font-semibold">
      {{ t('pages.marketingFlow.marketingFlowEditPage.activationConfirmation') }}
    </p>

    <p class="mb-5 mt-2 text-sm text-foreground-secondary text-center">
      {{ t('pages.marketingFlow.marketingFlowEditPage.activationDisclaimer') }}
    </p>

    <div class="flex w-full flex-row justify-center">
      <Button
        v-if="showSaveAndQuitButton"
        variant="outline"
        class="mr-3 flex-1"
        :disabled="isProcessing"
        @click="emit('on-save-and-quit')"
      >
        <span class="text-foreground">{{ t('pages.marketingFlow.marketingFlowEditPage.saveAndQuitButton') }}</span>
      </Button>
      <Button v-else variant="outline" class="mr-3 flex-1" :disabled="isProcessing" @click="handleClose(false)">
        <span class="text-foreground">{{ t('global.words.cancel') }}</span>
      </Button>

      <Button variant="default-primary" class="flex-1" :disabled="isProcessing" @click="handleConfirm">
        <Loader2 v-if="isProcessing" class="animate-spin" />
        {{ t('pages.marketingFlow.marketingFlowEditPage.activateScenario') }}
      </Button>
    </div>
  </BaseModal>

  <BaseModal
    v-else
    :is-open="isOpen"
    size="lg"
    :linkText="t('pages.marketingFlow.marketingFlowEditPage.activationModal.segmentEntry.confirmButton')"
    :cancel="t('pages.marketingFlow.marketingFlowEditPage.activationModal.segmentEntry.cancelButton')"
    :is-processing="isProcessing"
    :is-closable="!isProcessing"
    @close="handleClose(false)"
    @click="handleConfirm"
  >
    <template #customHeadingTitle>
      <h3 class="font-semibold text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.activationModal.segmentEntry.title') }}
      </h3>
    </template>

    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-4 p-4 border border-border bg-muted rounded-lg">
        <div class="flex items-center gap-2 text-sm">
          <div class="bg-white border border-border p-2 rounded-xl shrink-0">
            <BaseIcon
              :name="selectedAudience?.icon || 'HeroUsersIconOutline'"
              class="h-5 w-5 text-foreground-secondary"
            />
          </div>
          <span class="font-medium text-foreground">{{ selectedAudience?.name }}</span>
        </div>
        <i18n-t
          tag="p"
          keypath="pages.marketingFlow.marketingFlowEditPage.activationModal.segmentEntry.audienceInfo"
          class="text-sm text-foreground-secondary inline"
        >
          <template #count>
            <span class="text-primary font-medium">
              {{ audienceCount ?? 0 }}
              {{
                t('pages.marketingFlow.marketingFlowEditPage.activationModal.segmentEntry.audienceInfoCount', {
                  count: audienceCount ?? 0
                })
              }}
            </span>
          </template>
          <template #suffix>
            {{ t('pages.marketingFlow.marketingFlowEditPage.activationModal.segmentEntry.audienceInfoCountSuffix') }}
          </template>
        </i18n-t>
      </div>

      <p class="font-medium text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.activationModal.segmentEntry.question') }}
      </p>

      <AppRadioCard v-model="executeOnExistingMembers" class="grid grid-cols-1 gap-2.5" :options="choices" />
    </div>
  </BaseModal>
</template>
